import { Box, ErrorModal, Text, useOpenModal } from '@modusign/design-system';
import { useAttachments, useDocument, useSigner, useSigners } from '@modusign/react-signing';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useToggle } from 'react-use';

import { FullWidthButtonAlertModalWithCheckbox } from '@app/components/Modal/FullWidthButtonAlertModalWithCheckbox';
import IdentificationModal from '@app/containers/Modal/IdentificationModal';
import RequesterAttachmentsCard from '@app/features/apply-react-signing/components/RequesterAttachmentCard';
import ContractLinkShareModal from '@app/features/contractLink/components/ContractLinkShareModal';
import {
  ContractLinkCreateFooter,
  ContractLinkCreateInfoCard,
  ContractLinkCreateInfoModal,
  ContractLinkCreateSignerAuthCard,
  getLabels,
  getMaxSigningPerParticipant,
  getMaxSigningPerParticipantLimit,
} from '@app/features/contractLink/contractLinkCreate';
import { useCreateContractLink } from '@app/features/contractLink/contractLinkCreate/hooks/useCreateContractLink';
import useUserIdentificationAndDenyInfo from '@app/hooks/user/useUserIdentificationAndDenyInfo';
import useUsageCount from '@app/hooks/useUsageCount';
import { useUserScopedLocalStorage } from '@app/hooks/useUserScopedLocalStorage';
import coalesceGlobalErrorMessage from '@app/libs/coalesceGlobalErrorMessage';
import PDFDocumentContentLayout from '@app/libs/reactPdfJs/components/PDFDocumentContentLayout';
import { loadAccount } from '@app/store/account/actions';

interface ContractLinkEtcContentProps {
  onBackStep: () => void;
}

function ContractLinkEtcContent({ onBackStep }: ContractLinkEtcContentProps) {
  const history = useHistory();
  const dispatch = useDispatch();

  const { signers } = useSigners();
  // (@Juyeong-Byeon): 링크서명자는 1명이기 때문에 signers 중 첫번째 유저를 가져와서 사용한다.
  const contractLinkSigner = signers[0];
  const { setMessage } = useSigner(contractLinkSigner.id);
  const { attachments } = useAttachments();
  const { attachmentRequestCount } = useUsageCount();
  const { document, setTitle } = useDocument();

  const selectedLabels = useSelector(getLabels);
  const maxSigningPerParticipant = useSelector(getMaxSigningPerParticipant);
  const maxSigningPerParticipantLimit = useSelector(getMaxSigningPerParticipantLimit) ?? 0;

  const [maxSigningPerParticipantError, setMaxSigningPerParticipantError] = useState('');

  const [allowDuplicatedSign, toggleAllowDuplicatedSign] = useToggle(false);
  const [isMessageInputOpen, toggleIsMessageInputOpen] = useToggle(false);
  const [titleError, setTitleError] = useState('');

  const { mutateAsync: createContractLink } = useCreateContractLink();
  const { isNeedIdentification } = useUserIdentificationAndDenyInfo();

  const hasAttachmentLimitReached = attachmentRequestCount.limit <= attachments.length;

  const completeDisabled =
    !!titleError ||
    !!maxSigningPerParticipantError ||
    (allowDuplicatedSign && !maxSigningPerParticipant);

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const openModal = useOpenModal();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [disableContractLinkLabelAlertModal, setDisableContractLinkLabelAlertModal] =
    useUserScopedLocalStorage('contractLinkLabelAlertModal', false);

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    if (!isMessageInputOpen) {
      setMessage('');
    }

    try {
      const { id, title } = await createContractLink();
      // TODO(@Andy): 링크서명 생성 통합테스트 작성하기
      openModal(
        ({ isOpen, onClose }) => (
          <ContractLinkShareModal
            contractLinkId={id}
            contractLinkTitle={title}
            isOpen={isOpen}
            variant="AFTER_CREATE"
            onClose={onClose}
          />
        ),
        { keepOnUnmount: true }
      );
      history.replace('/links');
    } catch (e) {
      setErrorMessage(coalesceGlobalErrorMessage(e.message || ''));
    } finally {
      setIsLoading(false);
    }
  }, [isMessageInputOpen, createContractLink, setMessage, openModal, history]);

  const handleNextStepClick = useCallback(async () => {
    if (isNeedIdentification) {
      openModal((modalProps) => <IdentificationModal {...modalProps} />);
      return;
    }

    setIsInfoModalOpen(true);
  }, [openModal, isNeedIdentification]);

  useEffect(() => {
    dispatch(loadAccount());
  }, [dispatch]);

  return (
    <>
      <PDFDocumentContentLayout overflow="auto">
        <Box flex="1" minHeight="0">
          <Box maxWidth="xs" mx="auto" pb="86px" pt={4} px={4}>
            <ContractLinkCreateInfoCard
              allowDuplicatedSign={{
                value: allowDuplicatedSign,
                toggle: toggleAllowDuplicatedSign,
              }}
              maxSigningPerParticipant={{
                value: maxSigningPerParticipant,
                limit: maxSigningPerParticipantLimit,
                error: maxSigningPerParticipantError,
                setError: setMaxSigningPerParticipantError,
              }}
              messageInput={{
                isOpen: isMessageInputOpen,
                toggle: toggleIsMessageInputOpen,
              }}
              signerId={contractLinkSigner.id}
              title={{
                value: document.title,
                error: titleError,
                setError: setTitleError,
                onChangeValue: (v) => {
                  setTitle(v);
                },
              }}
            />
            <ContractLinkCreateSignerAuthCard signerId={contractLinkSigner.id} />
            <RequesterAttachmentsCard
              cardInnerPadding={7}
              hasAttachmentLimitReached={hasAttachmentLimitReached}
              headerFontSize="18px"
              headerFontWeight="bold"
              headerMarginBottom={6}
            />
          </Box>
        </Box>
      </PDFDocumentContentLayout>
      <ContractLinkCreateFooter
        backButtonText="< 이전 단계로"
        disabled={completeDisabled}
        nextButtonText="설정 완료 >"
        onBackStep={onBackStep}
        onNextStep={handleNextStepClick}
      />

      {isInfoModalOpen && (
        <ContractLinkCreateInfoModal
          isLoading={isLoading}
          isOpen={isInfoModalOpen}
          signerId={contractLinkSigner.id}
          onClose={() => setIsInfoModalOpen(false)}
          onSubmit={() => {
            if (selectedLabels.length === 0 && !disableContractLinkLabelAlertModal) {
              openModal((modalProps) => (
                <FullWidthButtonAlertModalWithCheckbox
                  {...modalProps}
                  cancelButtonLabel="라벨 설정"
                  checkBoxLabel="이 알림 다시 보지 않기"
                  confirmButtonLabel="링크서명 만들기"
                  title="라벨 미설정"
                  onCancel={() => {
                    modalProps.onClose();
                  }}
                  onCheckBoxChange={setDisableContractLinkLabelAlertModal}
                  onConfirm={() => {
                    handleSubmit();
                    modalProps.onClose();
                  }}
                >
                  <Text fontSize="md" fontWeight="regular" textAlign="center">
                    라벨이 없으면 문서 분류에 어려움이 발생할 수 있습니다.
                    <br />
                    라벨을 설정하지 않고 링크서명을 만드시겠어요?
                  </Text>
                </FullWidthButtonAlertModalWithCheckbox>
              ));
            } else {
              handleSubmit();
            }
          }}
        />
      )}
      <ErrorModal
        closeButtonLabel="확인"
        error={errorMessage}
        isOpen
        title="링크 만들기 실패"
        onClose={() => setErrorMessage(null)}
      />
    </>
  );
}

export default ContractLinkEtcContent;
